import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Group } from '@cobu/api-client';

// Define the groups state type
export interface GroupsState {
    groups: Record<string, Group>;
    isLoading: boolean;
    error: string | null;
    lastFetch: number | null;
}

// Initial state
const initialState: GroupsState = {
    groups: {},
    isLoading: false,
    error: null,
    lastFetch: null,
};

// Create groups slice with reducers
const groupsSlice = createSlice({
    name: 'groups',
    initialState,
    reducers: {
        // Set loading state
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },

        // Set groups data
        setGroups: (state, action: PayloadAction<Group[]>) => {
            const groupsMap = action.payload.reduce((acc, group) => {
                acc[group.uuid] = group;
                return acc;
            }, {} as Record<string, Group>);

            state.groups = groupsMap;
            state.isLoading = false;
            state.error = null;
            state.lastFetch = Date.now();
        },

        // Update group membership status
        updateGroupMembership: (state, action: PayloadAction<{ groupId: string; isMember: boolean }>) => {
            const { groupId, isMember } = action.payload;
            const group = state.groups[groupId];

            if (group) {
                state.groups[groupId] = {
                    ...group,
                    meta: {
                        ...group.meta,
                        userIsMember: isMember,
                    },
                };
            }
        },

        // Set error state
        setError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.isLoading = false;
        },

        // Clear error state
        clearError: (state) => {
            state.error = null;
        },

        // Reset to initial state
        reset: () => initialState,
    },
});

// Export actions and reducer
export const {
    setLoading,
    setGroups,
    updateGroupMembership,
    setError,
    clearError,
    reset
} = groupsSlice.actions;

export default groupsSlice.reducer; 